import type { UseSoftphoneContactReturn } from './index.types';
import { MobXProviderContext } from 'mobx-react';
import type { ContactModelV2 } from 'Models/ContactModelV2';
import { resolveCallContactInfo } from 'Modules/softphone/resolver/contactResolver';
import { useSoftphoneStore } from 'Modules/softphone/store';
import type { CallSession } from 'Modules/softphone/types';
import { isPhoneNumberString } from 'Modules/softphone/utils/validation';
import { useContext, useEffect, useRef } from 'react';
import type { RootStoreProps } from 'Stores/RootStore.types';

/**
 * useSoftphoneContact - Hook to lookup contact information for a call
 *
 * For internal calls (with personId): uses PersonStore cache (same as sidebar)
 * For external calls: uses PersonStore.getAllContactsWithSameNum (same as legacy)
 *
 * Uses the same approach as useEntityInfo/useEntityDetails for instant lookup
 * from the MobX PersonStore cache, which is kept up-to-date via Pusher.
 *
 * Caches the last resolved contact data so that when a call is removed from the
 * store (after cleanup delay), the UI still shows the correct avatar/name until
 * the softphone component fully unmounts. This prevents the brief flash of a
 * default avatar icon in fixed mode (CUU2-3319).
 *
 * @param callId - Optional call ID to look up. Falls back to currentCallId.
 * @param fallbackCall - Optional CallSession object used as a last-resort
 *   fallback when the call is not found in the store (CUU2-3365). This handles
 *   the race condition during layout transitions where a virtual conference-held
 *   entry is removed from the store by the old component's useEffect cleanup
 *   before the new component's useEffect re-adds it.
 */
export const useSoftphoneContact = (
  callId?: string,
  fallbackCall?: Pick<CallSession, 'displayName' | 'phoneNumber'>,
): UseSoftphoneContactReturn => {
  const { activeCalls, currentCallId } = useSoftphoneStore((state) => ({
    activeCalls: state.activeCalls,
    currentCallId: state.currentCallId,
  }));

  // Use provided callId or fall back to currentCallId
  const targetCallId = callId ?? currentCallId;
  const currentCall = targetCallId ? activeCalls.get(targetCallId) : null;

  const { person: personStore } = useContext(
    MobXProviderContext
  ) as RootStoreProps;

  const rawPhoneNumber = currentCall?.phoneNumber;
  const personId = currentCall?.personId;

  const isInternalCall = !!personId;

  // For internal calls, load person by ID
  useEffect(() => {
    if (isInternalCall && personId) {
      void personStore?.loadPersonByIdGetIfMissingGet(personId);
    }
  }, [isInternalCall, personId, personStore]);

  // Get person for internal calls
  const person: ContactModelV2 | null =
    isInternalCall && personId
      ? personStore?.selectPersonValueById(Number(personId)) ?? null
      : null;

  // One shared lookup, also used by CallAnnouncer, so the name on screen and
  // the name announced are the same by construction.
  const {
    contact,
    displayName,
    phoneNumber,
    avatarUrl: image,
  } = currentCall
    ? resolveCallContactInfo(personStore, currentCall)
    : { contact: null, displayName: undefined, phoneNumber: undefined, avatarUrl: undefined };

  // Cache the last valid contact data to prevent avatar flash when the call
  // is removed from the store but the UI hasn't unmounted yet (CUU2-3319).
  const cachedResultRef = useRef<UseSoftphoneContactReturn>({
    contact: null,
    displayName: undefined,
    phoneNumber: undefined,
    image: undefined,
    isLoading: false,
  });

  const result: UseSoftphoneContactReturn = {
    contact,
    displayName,
    phoneNumber,
    image,
    isLoading: isInternalCall ? !person : false,
  };

  // Update cache when we have valid contact data from an active call
  if (currentCall) {
    cachedResultRef.current = result;
  }

  // If the call was removed but the component is still mounted,
  // return the cached data to keep the avatar stable until unmount
  const hasCachedData =
    cachedResultRef.current.displayName || cachedResultRef.current.phoneNumber;
  if (!currentCall && hasCachedData) {
    return cachedResultRef.current;
  }

  // CUU2-3365: If the call is not in the store and there's no cache (e.g. fresh
  // mount after layout transition), use the fallbackCall props as last resort.
  // This avoids every consumer needing its own fallback chain.
  if (!currentCall && !hasCachedData && fallbackCall) {
    const fallbackDisplayName = isPhoneNumberString(fallbackCall.displayName)
      ? undefined
      : fallbackCall.displayName || undefined;
    const fallbackPhoneNumber = fallbackCall.phoneNumber || undefined;

    if (fallbackDisplayName || fallbackPhoneNumber) {
      return {
        contact: null,
        displayName: fallbackDisplayName,
        phoneNumber: fallbackPhoneNumber,
        image: undefined,
        isLoading: false,
      };
    }
  }

  return result;
};
